import { z } from "zod";
import { COMPANY_STATUSES, MAX_TEXT_LONG, MAX_TEXT_SHORT, RADIUS_KM_MAX } from "../constants.js";
import { paginationQuerySchema } from "./common.js";
import { industryCodeSchema } from "./industry.js";

// ── 入力の部品 ──
const companyProfileFields = {
  name: z.string().trim().min(1, "企業名は必須です").max(MAX_TEXT_SHORT),
  area: z.string().trim().min(1, "所在地（エリア）は必須です").max(MAX_TEXT_SHORT),
  // 業種はマスタから選ぶ（§2.5）。ここは code の形式のみ検証。存在・is_active はサーバの責務（§4.1）。
  industryCode: industryCodeSchema,
  jobTypes: z
    .array(z.string().trim().min(1).max(MAX_TEXT_SHORT))
    .min(1, "職種を1つ以上入力してください"),
  description: z.string().trim().min(1, "仕事内容の説明は必須です").max(MAX_TEXT_LONG),
  capacity: z.coerce.number().int().min(1, "受入可能人数は1以上で入力してください").max(1000),
  conditions: z.string().trim().max(MAX_TEXT_LONG).default(""),
  contactName: z.string().trim().min(1, "担当者名は必須です").max(MAX_TEXT_SHORT),
  contactEmail: z.string().email("担当者メールの形式が正しくありません"),
  contactPhone: z
    .string()
    .trim()
    .max(MAX_TEXT_SHORT)
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  // 所在地の座標（地図でピンを置いた地点・F-16/§2.6）。任意（NULL可）。
  // 6桁（≒0.1m）に丸めて過剰桁を弾く。範囲はここ＋DB CHECK の二重防御。
  latitude: z
    .number()
    .finite()
    .min(-90)
    .max(90)
    .transform((v) => Math.round(v * 1e6) / 1e6)
    .nullish(),
  longitude: z
    .number()
    .finite()
    .min(-180)
    .max(180)
    .transform((v) => Math.round(v * 1e6) / 1e6)
    .nullish(),
  isPublic: z.boolean().default(true),
};

// 緯度・経度は「両方指定」か「両方なし」（片方だけは不可・§2.6）。
// companyProfileFields を使う各スキーマに適用する。
function coordsBothOrNeither(
  v: { latitude?: number | null; longitude?: number | null },
  ctx: z.RefinementCtx,
): void {
  if ((v.latitude == null) !== (v.longitude == null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "地図のピンは緯度・経度の両方が必要です（片方だけは指定できません）",
      path: ["latitude"],
    });
  }
}

// 企業の受入情報 登録/更新（企業自身 or 管理者）
export const companyProfileInputSchema = z
  .object(companyProfileFields)
  .superRefine(coordsBothOrNeither);
export type CompanyProfileInput = z.infer<typeof companyProfileInputSchema>;

// 企業の自己登録（アカウント＋受入情報を同時に作る。guest）
export const companyRegisterInputSchema = z
  .object({
    ...companyProfileFields,
    // ログインアカウント
    loginEmail: z.string().email("ログイン用メールの形式が正しくありません"),
    password: z
      .string()
      .min(12, "パスワードは12文字以上にしてください") // [19] §6.6
      .max(200),
  })
  // 自己登録では公開状態は審査後に管理者が決めるため無視される（サーバ側で pending 固定）
  .omit({ isPublic: true })
  .superRefine(coordsBothOrNeither);
export type CompanyRegisterInput = z.infer<typeof companyRegisterInputSchema>;

// 管理者による代理登録（審査済みで作成。ログインアカウントは任意）
export const companyAdminCreateInputSchema = z
  .object({
    ...companyProfileFields,
    loginEmail: z.string().email().optional(),
    password: z.string().min(12).max(200).optional(),
  })
  .superRefine(coordsBothOrNeither);
export type CompanyAdminCreateInput = z.infer<typeof companyAdminCreateInputSchema>;

// ── 出力（allowlist・[19] §1.2）──

// 公開項目のみ（ログイン不要で返す4項目 + id）。座標は公開情報（F-17/F-18・§2.6）。
export const companyPublicSchema = z.object({
  id: z.string(),
  name: z.string(),
  area: z.string(),
  // 業種はマスタ参照（§4.1）。code と label の両方を返す。label は industries を JOIN して
  // 解決し is_active で絞らない（廃止済みでもラベルが出て業種欄が空/生コードにならない）。
  industryCode: z.string(),
  industryLabel: z.string(),
  jobTypes: z.array(z.string()),
  description: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type CompanyPublic = z.infer<typeof companyPublicSchema>;

// 詳細（ログイン後・学校/自社/管理者）。公開項目＋限定項目。
export const companyDetailSchema = companyPublicSchema.extend({
  capacity: z.number().int(),
  conditions: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string().nullable(),
  status: z.enum(COMPANY_STATUSES),
  isPublic: z.boolean(),
});
export type CompanyDetail = z.infer<typeof companyDetailSchema>;

// ── 検索クエリ ──
export const companySearchQuerySchema = paginationQuerySchema.extend({
  area: z.string().trim().optional(),
  // 業種は code の完全一致で絞る（§4.1）。存在チェックはサーバ（不正値は 400・非活性は許容）。
  industryCode: z.string().trim().optional(),
  q: z.string().trim().max(MAX_TEXT_SHORT).optional(),
  sort: z.enum(["created_at", "name"]).default("created_at"),
  order: z.enum(["asc", "desc"]).default("desc"),
});
export type CompanySearchQuery = z.infer<typeof companySearchQuerySchema>;

// ── 地図（F-18 全体地図・F-19 半径検索・§2.6）──

// 全体地図の1ピン。座標は必ずある（サーバが「座標あり」で絞る）。
export const companyMapPinSchema = z.object({
  id: z.string(),
  name: z.string(),
  area: z.string(),
  industryLabel: z.string(), // 地図の吹き出し表示用（§4.1 実装メモ: industry→industryLabel）
  latitude: z.number(),
  longitude: z.number(),
});
export type CompanyMapPin = z.infer<typeof companyMapPinSchema>;

// 全体地図APIの応答。ハードキャップ超過は capped=true（先頭 cap 件＋絞り込み案内）。
export const companyMapResponseSchema = z.object({
  items: z.array(companyMapPinSchema),
  capped: z.boolean(),
  cap: z.number().int(),
});
export type CompanyMapResponse = z.infer<typeof companyMapResponseSchema>;

// 半径検索クエリ（F-19）。中心＋半径km。radiusKm は 0 < r <= 上限（範囲外は 400）。
// エリア・業種・キーワードは公開一覧と同じ絞り込みを併用できる（§2.6）。
//
// **空文字を明示的に拒否**する。`z.coerce.number("")` は 0 になるため、`?latitude=&longitude=`
// のような空パラメータが (0,0) として通り 400 にならない（§4.1 の境界検証に反する）。
// 数値へ coerce する前に「空文字・null」を undefined 扱いにする（欠落=undefined と同じく
// 検証失敗＝400 になる）。クエリは常に string だが、共有スキーマが JSON body に流用されて
// `null→0` に化けないよう null も弾く（数値はそのまま通す＝JSON の座標も有効）。
const rejectEmpty = (v: unknown) =>
  v == null || (typeof v === "string" && v.trim() === "") ? undefined : v;
export const companyRadiusQuerySchema = paginationQuerySchema.extend({
  latitude: z.preprocess(rejectEmpty, z.coerce.number().finite().min(-90).max(90)),
  longitude: z.preprocess(rejectEmpty, z.coerce.number().finite().min(-180).max(180)),
  radiusKm: z.preprocess(rejectEmpty, z.coerce.number().finite().gt(0).max(RADIUS_KM_MAX)),
  area: z.string().trim().optional(),
  industryCode: z.string().trim().optional(),
  q: z.string().trim().max(MAX_TEXT_SHORT).optional(),
});
export type CompanyRadiusQuery = z.infer<typeof companyRadiusQuerySchema>;

// 半径検索の1件。公開項目＋中心からの距離km（表示用・小数1桁）。
export const companyNearbySchema = companyPublicSchema.extend({
  distanceKm: z.number(),
});
export type CompanyNearby = z.infer<typeof companyNearbySchema>;

// 審査（管理者）
export const companyRejectInputSchema = z.object({
  reason: z.string().trim().max(MAX_TEXT_LONG).optional(),
});
export type CompanyRejectInput = z.infer<typeof companyRejectInputSchema>;
